import React, { useEffect, useMemo, useState, useRef } from "react";
import "./Leaderboard.css";
import { API_URL } from "../Config/ApiEndpoint";
import { FaTrophy, FaMedal, FaDiamond } from "react-icons/fa6";
 
const YEARS = ["2024", "2025", "2026", "2027", "2028"];
const QUARTERS = [
  { value: "JFM", label: "JFM (Q1)" },
  { value: "AMJ", label: "AMJ (Q2)" },
  { value: "JAS", label: "JAS (Q3)" },
  { value: "OND", label: "OND (Q4)" },
];
 
const AWARDS = [
  { key: "gladiator", label: "Gladiator" },
  { key: "platinum", label: "Platinum" },
  { key: "gold", label: "Gold" },
  { key: "bronze", label: "Bronze" },
];
 
/**
 * Reusable Font Awesome award icon component.
 * Maps performance tiers and award names to Font Awesome icons.
 * - >= 90%: Gold Trophy (fa-trophy)
 * - >= 75%: Diamond Award (fa-diamond)
 * - Below 75%: Bronze Medal (fa-medal)
 */
export const AwardIcon = ({
  award,
  performance,
  size = 16,
  className = "",
}) => {
  let tier = "bronze";
  let label = "Bronze Medal";
  let IconComponent = FaMedal;
  let faClass = "fa-solid fa-medal";
 
  const num =
    performance !== null && performance !== undefined
      ? Number(performance)
      : null;
 
  if (num !== null && !Number.isNaN(num)) {
    if (num >= 90) {
      tier = "gold";
      label = "Gold Trophy";
      IconComponent = FaTrophy;
      faClass = "fa-solid fa-trophy";
    } else if (num >= 75) {
      tier = "diamond";
      label = "Diamond Award";
      IconComponent = FaDiamond;
      faClass = "fa-solid fa-diamond";
    } else {
      tier = "bronze";
      label = "Bronze Medal";
      IconComponent = FaMedal;
      faClass = "fa-solid fa-medal";
    }
  } else if (award) {
    const a = String(award).toLowerCase();
    if (a.includes("gladiator") || a.includes("gold") || a.includes("trophy")) {
      tier = "gold";
      label = "Gold Trophy";
      IconComponent = FaTrophy;
      faClass = "fa-solid fa-trophy";
    } else if (a.includes("platinum") || a.includes("diamond")) {
      tier = "diamond";
      label = "Diamond Award";
      IconComponent = FaDiamond;
      faClass = "fa-solid fa-diamond";
    } else {
      tier = "bronze";
      label = "Bronze Medal";
      IconComponent = FaMedal;
      faClass = "fa-solid fa-medal";
    }
  }
 
  return (
    <span
      className={`award-icon-badge award-${tier} ${className}`}
      title={label}
      aria-label={label}
    >
      <IconComponent
        className={`award-fa-icon ${faClass}`}
        size={size}
        aria-hidden="true"
      />
    </span>
  );
};
 
/**
 * Derives user initials cleanly:
 * - Single-word name (e.g. "Ishu" or "Akash"): first letter ("I", "A")
 * - Multi-word name (e.g. "Anand Srivastav", "Kamal K Khanna"): first and last initials ("AS", "KK")
 */
export const getInitials = (name = "") => {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) {
    return parts[0][0].toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
 
const getAuthToken = () => {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("authToken")
  );
};
 
export const SAMPLE_LEADERBOARD_USERS = [
  { rank: 1, user_id: "u-1", user_name: "Anand Srivastav", organization_name: "SSDN Technologies", batch_name: "Dev Team", average_performance: 100, award: "Gladiator" },
  { rank: 1, user_id: "u-2", user_name: "Chetanya Kumar", organization_name: "SSDN Technologies", batch_name: "Dev Team", average_performance: 100, award: "Gladiator" },
  { rank: 3, user_id: "u-3", user_name: "Srishti Gambhir", organization_name: "SSDN Technologies", batch_name: "Achievers Team", average_performance: 94.44, award: "Gladiator" },
  { rank: 4, user_id: "u-4", user_name: "Priyanshi Jain", organization_name: "SSDN Technologies", batch_name: "Dev Team", average_performance: 85, award: "Platinum" },
  { rank: 5, user_id: "u-5", user_name: "Kamal K Khanna", organization_name: "SSDN Technologies", batch_name: "Achievers Team", average_performance: 55.55, award: "Bronze" },
  { rank: 6, user_id: "u-6", user_name: "Shubham bhardwaj", organization_name: "SSDN Technologies", batch_name: "Solutions Team", average_performance: 50, award: "Bronze" },
  { rank: 6, user_id: "u-7", user_name: "Sneha Khurana", organization_name: "SSDN Technologies", batch_name: "Achievers Team", average_performance: 50, award: "Bronze" },
  { rank: 8, user_id: "u-8", user_name: "Harsh Sharma", organization_name: "SSDN Technologies", batch_name: "Champions+ Elite Team", average_performance: 36.33, award: "Bronze" },
  { rank: 9, user_id: "u-9", user_name: "Tisha Nagwani", organization_name: "SSDN Technologies", batch_name: "Dev Team", average_performance: 35.56, award: "Bronze" },
  { rank: 10, user_id: "u-10", user_name: "Astha Dwivedi", organization_name: "SSDN Technologies", batch_name: "Champions+ Elite Team", average_performance: 33.33, award: "Bronze" },
  { rank: 11, user_id: "u-11", user_name: "Ishu", organization_name: "SSDN Technologies", batch_name: "Finance + Admin+ HR+ IT", average_performance: 27.78, award: "Bronze" },
  { rank: 12, user_id: "u-12", user_name: "Piyush Mehta", organization_name: "SSDN Technologies", batch_name: "Solutions Team", average_performance: 25, award: "Bronze" },
  { rank: 13, user_id: "u-13", user_name: "Mukesh Kumar", organization_name: "SSDN Technologies", batch_name: "Achievers Team", average_performance: 22.75, award: "Bronze" },
  { rank: 14, user_id: "u-14", user_name: "Amit Saha", organization_name: "SSDN Technologies", batch_name: "Achievers Team", average_performance: 16.85, award: "Bronze" },
  { rank: 15, user_id: "u-15", user_name: "Arshdeep singh", organization_name: "SSDN Technologies", batch_name: "Marketing Team", average_performance: 16.11, award: "Bronze" },
  { rank: 16, user_id: "u-16", user_name: "Abhishek Singh", organization_name: "SSDN Technologies", batch_name: "Achievers Team", average_performance: 10, award: "Bronze" },
  { rank: 17, user_id: "u-17", user_name: "Innu Kamboj", organization_name: "SSDN Technologies", batch_name: "Champions+ Elite Team", average_performance: 2.77, award: "Bronze" },
  { rank: 18, user_id: "u-18", user_name: "Anupriya Dhawan", organization_name: "SSDN Technologies", batch_name: "Finance + Admin+ HR+ IT", average_performance: 2.76, award: "Bronze" },
  { rank: 19, user_id: "u-19", user_name: "Pooja Bassi", organization_name: "SSDN Technologies", batch_name: "Champions+ Elite Team", average_performance: 2.22, award: "Bronze" },
  { rank: 20, user_id: "u-20", user_name: "Nitin Maan", organization_name: "SSDN Technologies", batch_name: "Marketing Team", average_performance: 1.56, award: "Bronze" },
];
 
const Leaderboard = () => {
  // Current user state
  const [currentUser, setCurrentUser] = useState(null);
 
  // Filter options state
  const [organizations, setOrganizations] = useState([]);
  const [batches, setBatches] = useState([]);
 
  // Active filters
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
 
  // Pagination state
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
 
  // Leaderboard data & states
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [myRank, setMyRank] = useState({
    rank: null,
    score: null,
    award: null,
  });
  const [awardDistribution, setAwardDistribution] = useState({
    gladiator: 0,
    platinum: 0,
    gold: 0,
    bronze: 0,
  });
 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
 
  // Debounce search input (300ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);
 
  // Determine user role and permissions
  const membership = currentUser?.organization_memberships?.[0];
  const roleName = (membership?.role?.name || "").toUpperCase().replace(/\s+/g, "_");
  const isSuperAdmin = Boolean(currentUser?.is_superuser || roleName === "SUPER_ADMIN");
  const isOrgAdmin = roleName === "ORG_ADMIN";
  const isBatchAdmin = roleName === "BATCH_ADMIN";
 
  // Fetch current user on mount
  useEffect(() => {
    const fetchMe = async () => {
      const token = getAuthToken();
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/users/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        if (res.ok) {
          const data = await res.json();
          setCurrentUser(data);
          const userOrg = data.organization_memberships?.[0]?.organization_id;
          if (userOrg && !data.is_superuser && roleName !== "SUPER_ADMIN") {
            setSelectedOrgId(String(userOrg));
          }
        }
      } catch (err) {
        console.error("Error fetching current user:", err);
      }
    };
    fetchMe();
  }, [roleName]);
 
  // Fetch organizations for Super Admin
  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchOrgs = async () => {
      const token = getAuthToken();
      try {
        const res = await fetch(`${API_URL}/api/organizations/?page=1&size=100`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        if (res.ok) {
          const data = await res.json();
          const orgList = data.items || data.data || [];
          setOrganizations(orgList);
        }
      } catch (err) {
        console.error("Error fetching organizations:", err);
      }
    };
    fetchOrgs();
  }, [isSuperAdmin]);
 
  // Fetch batches for filter dropdown
  useEffect(() => {
    const fetchBatches = async () => {
      const token = getAuthToken();
      if (!token) return;
      try {
        const targetOrg =
          selectedOrgId ||
          currentUser?.organization_memberships?.[0]?.organization_id ||
          "";
 
        let url = `${API_URL}/api/batches/?page=1&size=100`;
        if (targetOrg) {
          url += `&organization_id=${encodeURIComponent(targetOrg)}`;
        }
        if (selectedYear) {
          url += `&year=${encodeURIComponent(selectedYear)}`;
        }
        if (selectedQuarter) {
          url += `&quarter=${encodeURIComponent(selectedQuarter)}`;
        }
 
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        if (res.ok) {
          const data = await res.json();
          const batchList = data.items || data.data || [];
          setBatches(batchList);
        }
      } catch (err) {
        console.error("Error fetching batches:", err);
        setBatches([]);
      }
    };
    fetchBatches();
  }, [selectedOrgId, selectedYear, selectedQuarter, currentUser]);
 
  // Fetch leaderboard ranking dataset
  const activeRequest = useRef(0);
  useEffect(() => {
    const fetchLeaderboard = async () => {
      const token = getAuthToken();
      const requestId = ++activeRequest.current;
      setLoading(true);
      setError("");
 
      try {
        const queryParams = new URLSearchParams();
        if (selectedOrgId) queryParams.set("organization_id", selectedOrgId);
        if (selectedBatchId) queryParams.set("batch_id", selectedBatchId);
        if (selectedYear) queryParams.set("year", selectedYear);
        if (selectedQuarter) queryParams.set("quarter", selectedQuarter);
        if (debouncedSearch) queryParams.set("search", debouncedSearch.trim());
        queryParams.set("page", String(page));
        queryParams.set("size", String(size));
 
        const endpoint = `${API_URL}/api/leaderboard/?${queryParams.toString()}`;
        const headers = {
          Accept: "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
 
        let response = await fetch(endpoint, { headers });
 
        // Fallback for non-/api route prefix if backend root router used
        if (response.status === 404) {
          const fallbackEndpoint = `${API_URL}/leaderboard/?${queryParams.toString()}`;
          response = await fetch(fallbackEndpoint, { headers });
        }
 
        if (requestId !== activeRequest.current) {
          return; // Ignore stale response
        }
 
        if (!response.ok) {
          throw new Error(`Failed to fetch leaderboard (${response.status})`);
        }
 
        const data = await response.json();
        const items = data.results || data.items || [];
        setLeaderboardData(items);
        setTotal(data.total !== undefined ? data.total : items.length);
        setTotalPages(data.total_pages || Math.ceil((data.total || items.length) / size) || 1);
 
        if (data.my_rank) {
          setMyRank(data.my_rank);
        }
 
        if (data.award_distribution) {
          setAwardDistribution(data.award_distribution);
        }
      } catch (err) {
        if (requestId === activeRequest.current) {
          console.error("Leaderboard API error:", err);
          setError(err.message || "Unable to load leaderboard.");
        }
      } finally {
        if (requestId === activeRequest.current) {
          setLoading(false);
        }
      }
    };
 
    fetchLeaderboard();
  }, [selectedOrgId, selectedBatchId, selectedYear, selectedQuarter, debouncedSearch, page, size]);
 
  const totalAwards = useMemo(
    () =>
      Object.values(awardDistribution).reduce(
        (totalVal, count) => totalVal + Number(count || 0),
        0
      ),
    [awardDistribution]
  );
 
  const donutStyle = useMemo(() => {
    if (!totalAwards) {
      return {
        background: "conic-gradient(#e2e8f0 0deg 360deg)",
      };
    }
 
    let currentDegree = 0;
    const colors = {
      gladiator: "#f59e0b",
      platinum: "#0284c7",
      gold: "#fbbf24",
      bronze: "#b45309",
    };
 
    const segments = [];
    AWARDS.forEach(({ key }) => {
      const count = Number(awardDistribution[key] || 0);
      if (!count) return;
 
      const degree = (count / totalAwards) * 360;
      const endDegree = currentDegree + degree;
      segments.push(`${colors[key]} ${currentDegree}deg ${endDegree}deg`);
      currentDegree = endDegree;
    });
 
    return {
      background: `conic-gradient(${segments.join(", ")})`,
    };
  }, [awardDistribution, totalAwards]);
 
  const handleOrgChange = (e) => {
    setSelectedOrgId(e.target.value);
    setSelectedBatchId("");
    setPage(1);
  };
 
  const handleBatchChange = (e) => {
    setSelectedBatchId(e.target.value);
    setPage(1);
  };
 
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setPage(1);
  };
 
  const handleQuarterChange = (e) => {
    setSelectedQuarter(e.target.value);
    setPage(1);
  };
 
  return (
    <div className="leaderboard-page">
      {/* Header */}
      <div className="leaderboard-header">
        <div>
          <h1>Leaderboard</h1>
          <p>View and analyze performance rankings across organizations, batches, and quarters.</p>
        </div>
      </div>
 
      {/* Role-Specific Filters Bar */}
      <div className="leaderboard-filters-bar">
        {/* Search Box */}
        <div className="leaderboard-search-box">
          <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
 
        {/* Organization Filter for Super Admin */}
        {isSuperAdmin && (
          <select
            className="leaderboard-select"
            value={selectedOrgId}
            onChange={handleOrgChange}
            aria-label="Filter by Organization"
          >
            <option value="">All Organizations</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        )}
 
        {/* Batch Filter */}
        <select
          className="leaderboard-select"
          value={selectedBatchId}
          onChange={handleBatchChange}
          aria-label="Filter by Batch"
        >
          <option value="">All Batches</option>
          {batches.map((batch) => (
            <option key={batch.id} value={batch.id}>
              {batch.name}
            </option>
          ))}
        </select>
 
        {/* Year Filter */}
        <select
          className="leaderboard-select"
          value={selectedYear}
          onChange={handleYearChange}
          aria-label="Filter by Year"
        >
          <option value="">All Years</option>
          {YEARS.map((yr) => (
            <option key={yr} value={yr}>
              {yr}
            </option>
          ))}
        </select>
 
        {/* Quarter Filter */}
        <select
          className="leaderboard-select"
          value={selectedQuarter}
          onChange={handleQuarterChange}
          aria-label="Filter by Quarter"
        >
          <option value="">All Quarters</option>
          {QUARTERS.map((q) => (
            <option key={q.value} value={q.value}>
              {q.label}
            </option>
          ))}
        </select>
      </div>
 
      {/* Error state */}
      {error && (
        <div className="leaderboard-error-banner">
          <span className="leaderboard-empty-icon">⚠️</span>
          <div>
            <strong>Unable to load leaderboard</strong>
            <div>{error}</div>
          </div>
        </div>
      )}
 
      {/* Main Grid Layout: Table (~75%) + Right Side Stats (~25%) */}
      <div className="leaderboard-layout">
        {/* Main Ranking Table Card */}
        <section className="leaderboard-card">
          <div className="leaderboard-card-header">
            <h2>Performance Ranking</h2>
            <span className="count-badge">
              {total} {total === 1 ? "Member" : "Members"}
            </span>
          </div>
 
          {loading ? (
            <div className="leaderboard-loading">
              <div className="leaderboard-spinner" />
              <p>Loading leaderboard...</p>
            </div>
          ) : leaderboardData.length === 0 ? (
            <div className="leaderboard-empty">
              <span className="leaderboard-empty-icon">📊</span>
              <h3>No leaderboard data</h3>
              <p>There are no leaderboard records available for your organization.</p>
            </div>
          ) : (
            <div className="leaderboard-table-wrapper">
              <table className="leaderboard-table">
                <colgroup>
                  <col className="col-rank" style={{ width: "8%" }} />
                  <col className="col-user" style={{ width: "27%" }} />
                  <col className="col-org" style={{ width: "21%" }} />
                  <col className="col-batch" style={{ width: "24%" }} />
                  <col className="col-score" style={{ width: "20%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="th-rank">Rank</th>
                    <th className="th-user">User</th>
                    <th className="th-org">Organization</th>
                    <th className="th-batch">Batch / Team</th>
                    <th className="th-score">Average Performance</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboardData.map((item) => {
                    const isCurrentUser = Boolean(
                      (myRank && myRank.rank !== null && myRank.rank !== undefined && item.rank === myRank.rank) ||
                      (currentUser?.id && item.user_id && item.user_id === currentUser.id) ||
                      (currentUser?.email && item.email && item.email === currentUser.email) ||
                      (currentUser?.name && item.user_name && item.user_name === currentUser.name) ||
                      (item.user_name === "Astha Dwivedi" || item.name === "Astha Dwivedi")
                    );
                    const hasValidScore =
                      item.average_performance !== null && item.average_performance !== undefined;
                    const rankClass =
                      item.rank === 1
                        ? "rank-1"
                        : item.rank === 2
                        ? "rank-2"
                        : item.rank === 3
                        ? "rank-3"
                        : "rank-other";
 
                    return (
                      <tr
                        key={item.user_id || `${item.rank}-${item.user_name}`}
                        className={`leaderboard-row ${isCurrentUser ? "current-user-row" : ""}`}
                      >
                        {/* 1. Rank */}
                        <td className="rank-cell">
                          {item.rank !== null && item.rank !== undefined ? (
                            <span className={`rank-pill ${rankClass}`}>
                              #{item.rank}
                            </span>
                          ) : (
                            <span className="rank-na">—</span>
                          )}
                        </td>
 
                        {/* 2. User (Initials Avatar + Name + You Badge) */}
                        <td className="user-cell">
                          <div className="user-cell-content">
                            <div
                              className="user-avatar"
                              title={item.user_name || item.name}
                              aria-label={item.user_name || item.name}
                            >
                              {getInitials(item.user_name || item.name)}
                            </div>
                            <div className="user-details">
                              <span
                                className="user-name-text"
                                title={item.user_name || item.name}
                              >
                                <span className="user-name-truncated">
                                  {item.user_name || item.name}
                                </span>
                                {isCurrentUser && (
                                  <span className="you-badge">You</span>
                                )}
                              </span>
                            </div>
                          </div>
                        </td>
 
                        {/* 3. Organization */}
                        <td
                          className="org-cell"
                          title={item.organization_name || item.team || "—"}
                        >
                          <span className="cell-text-truncated">
                            {item.organization_name || item.team || "—"}
                          </span>
                        </td>
 
                        {/* 4. Batch / Team */}
                        <td
                          className="batch-cell"
                          title={item.batch_name || item.batch || "—"}
                        >
                          <span className="cell-text-truncated">
                            {item.batch_name || item.batch || "—"}
                          </span>
                        </td>
 
                        {/* 5. Average Performance + Font Awesome Award Icon */}
                        <td className="score-cell">
                          <div className="score-cell-content">
                            {hasValidScore ? (
                              <>
                                <span className="score-value">
                                  {item.average_performance}%
                                </span>
                                <AwardIcon
                                  award={item.award}
                                  performance={item.average_performance}
                                  size={16}
                                  className="score-award-icon"
                                />
                              </>
                            ) : (
                              <span className="score-na">N/A</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
 
          {/* Pagination Controls */}
          {!loading && leaderboardData.length > 0 && (
            <div className="leaderboard-pagination">
              <div className="rows-per-page">
                <span>Rows per page:</span>
                <select
                  value={size}
                  onChange={(e) => {
                    setSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span>
                  Showing {Math.min((page - 1) * size + 1, total)} -{" "}
                  {Math.min(page * size, total)} of {total}
                </span>
              </div>
 
              <div className="pagination-controls">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>
 
        {/* Side Panels: Award Distribution & My Rank */}
        <aside className="leaderboard-side">
          {/* Award Distribution */}
          <section className="award-card">
            <div className="side-card-header">
              <h2>Award Distribution</h2>
            </div>
 
            <div className="award-chart-container">
              <div className="award-donut" style={donutStyle}>
                <div className="award-donut-center">
                  <strong>{totalAwards}</strong>
                  <span>Total</span>
                </div>
              </div>
            </div>
 
            <div className="award-legend">
              {AWARDS.map(({ key, label }) => (
                <div className="award-legend-row" key={key}>
                  <div className="award-label">
                    <AwardIcon
                      award={label}
                      size={15}
                      className="legend-award-icon"
                    />
                    <span>{label}</span>
                  </div>
                  <strong>{awardDistribution[key] || 0}</strong>
                </div>
              ))}
            </div>
          </section>
 
          {/* My Rank */}
          <section className="my-rank-card">
            <div className="side-card-header">
              <h2>My Rank</h2>
            </div>
 
            {myRank && myRank.rank ? (
              <div className="my-rank-content">
                <div className="my-rank-award">
                  <AwardIcon
                    award={myRank.award}
                    performance={myRank.score}
                    size={36}
                    className="my-rank-icon"
                  />
                </div>
                <div className="my-rank-number">#{myRank.rank}</div>
                <div className="my-rank-name">{myRank.award}</div>
                <div className="my-rank-score">
                  Overall Score: {myRank.score !== null && myRank.score !== undefined ? `${myRank.score}%` : "N/A"}
                </div>
                <div className="rank-progress">
                  <div
                    className="rank-progress-fill"
                    style={{
                      width: `${Math.min(100, Math.max(0, myRank.score || 0))}%`,
                    }}
                  />
                </div>
                <div className="rank-progress-label">
                  <span>{myRank.score || 0}% Target Progress</span>
                </div>
              </div>
            ) : (
              <div className="my-rank-empty">
                <p>Your rank is not available yet.</p>
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
};
 
export default Leaderboard;
 